import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useConnection } from "../context/ConnectionContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  BarChart3,
  Database,
  Server,
  Clock,
  Activity,
  Zap,
  TrendingUp,
  Layers,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Simple bar chart component
const MiniBarChart = ({ data, height = 60, color = "#22c55e" }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end gap-0.5" style={{ height }}>
      {data.map((d, i) => (
        <div
          key={i}
          className="flex-1 rounded-t transition-all hover:opacity-80"
          style={{
            height: `${(d.count / max) * 100}%`,
            backgroundColor: color,
            minHeight: d.count > 0 ? 2 : 0
          }}
          title={`${d.minute || d.range}: ${d.count}`}
        />
      ))}
    </div>
  );
};

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "primary" }) => (
  <Card className="bg-card/50">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${color}/10`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}% from last hour
        </div>
      )}
    </CardContent>
  </Card>
);

// Database type badge colors
const dbTypeColors = {
  mssql: "blue",
  mysql: "orange",
  postgresql: "sky",
  mongodb: "green",
  oracle: "red",
  sqlite: "gray",
  mariadb: "amber",
  redis: "rose",
  cassandra: "cyan",
  dynamodb: "yellow",
  couchbase: "pink",
  firebase: "amber"
};

const MetricsPage = () => {
  const { getAuthHeader } = useAuth();
  const { connections, selectedConnection: globalConnection } = useConnection();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connectionMetrics, setConnectionMetrics] = useState(null);
  const [loadingConnection, setLoadingConnection] = useState(false);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/metrics/overview`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      setOverview(response.data);
    } catch (error) {
      console.error("Failed to fetch metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectionMetrics = async (connectionId) => {
    if (!connectionId) {
      setConnectionMetrics(null);
      return;
    }
    
    setLoadingConnection(true);
    try {
      const response = await axios.get(`${API_URL}/metrics/connection/${connectionId}`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      setConnectionMetrics(response.data);
    } catch (error) {
      console.error("Failed to fetch connection metrics:", error);
    } finally {
      setLoadingConnection(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  // Default to current global connection
  useEffect(() => {
    if (globalConnection && !selectedConnection) {
      setSelectedConnection(globalConnection.id);
    }
  }, [globalConnection]);

  useEffect(() => {
    if (selectedConnection) {
      fetchConnectionMetrics(selectedConnection);
    }
  }, [selectedConnection]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || "0";
  };

  const formatTime = (ms) => {
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms?.toFixed(0) || 0}ms`;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              Connection Metrics
            </h1>
            <p className="text-muted-foreground">Monitor database performance and usage</p>
          </div>
          <Button variant="outline" onClick={fetchOverview} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            title="Total Connections"
            value={overview?.summary?.total_connections || 0}
            icon={Database}
            color="primary"
          />
          <StatCard
            title="Total Queries"
            value={formatNumber(overview?.summary?.total_queries || 0)}
            icon={Layers}
            color="blue-500"
          />
          <StatCard
            title="Queries/Min"
            value={overview?.summary?.queries_per_minute || 0}
            subtitle="Last hour average"
            icon={Activity}
            color="green-500"
          />
          <StatCard
            title="Rows Fetched"
            value={formatNumber(overview?.summary?.total_rows_fetched || 0)}
            icon={TrendingUp}
            color="amber-500"
          />
          <StatCard
            title="Avg Response"
            value={formatTime(overview?.summary?.avg_execution_time_ms || 0)}
            icon={Clock}
            color="purple-500"
          />
        </div>

        {/* Database Type Distribution */}
        {overview?.db_type_distribution && Object.keys(overview.db_type_distribution).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-4 h-4" />
                Connections by Database Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(overview.db_type_distribution).map(([type, count]) => (
                  <Badge 
                    key={type} 
                    variant="secondary"
                    className={`px-3 py-1 bg-${dbTypeColors[type] || 'gray'}-500/10 border-${dbTypeColors[type] || 'gray'}-500/20`}
                  >
                    <span className="font-medium">{type.toUpperCase()}</span>
                    <span className="ml-2 text-muted-foreground">{count}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Connection Selector */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Connection Performance</CardTitle>
                <CardDescription>Select a connection to view detailed metrics</CardDescription>
              </div>
              <Select value={selectedConnection || ""} onValueChange={setSelectedConnection}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select a connection" />
                </SelectTrigger>
                <SelectContent>
                  {overview?.connections?.map((conn) => (
                    <SelectItem key={conn.connection_id} value={conn.connection_id}>
                      <div className="flex items-center gap-2">
                        <Database className={`w-4 h-4 text-${dbTypeColors[conn.db_type] || 'gray'}-500`} />
                        {conn.connection_name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          
          {/* Connection Details */}
          {loadingConnection ? (
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </CardContent>
          ) : connectionMetrics ? (
            <CardContent className="space-y-6">
              {/* Connection Info */}
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className={`p-3 rounded-lg bg-${dbTypeColors[connectionMetrics.connection.db_type] || 'gray'}-500/10`}>
                  <Database className={`w-6 h-6 text-${dbTypeColors[connectionMetrics.connection.db_type] || 'gray'}-500`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{connectionMetrics.connection.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {connectionMetrics.connection.db_type.toUpperCase()} • {connectionMetrics.connection.host} • {connectionMetrics.connection.database}
                  </p>
                </div>
                <Badge variant={connectionMetrics.connection.status === 'connected' ? 'default' : 'secondary'}>
                  {connectionMetrics.connection.status}
                </Badge>
              </div>

              {/* Connection Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Queries</p>
                  <p className="text-xl font-bold">{formatNumber(connectionMetrics.stats.total_queries)}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                  <p className="text-xl font-bold text-green-500">{connectionMetrics.stats.success_rate}%</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Avg Response Time</p>
                  <p className="text-xl font-bold">{formatTime(connectionMetrics.stats.avg_execution_time_ms)}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Avg Rows/Query</p>
                  <p className="text-xl font-bold">{connectionMetrics.stats.avg_rows_per_query}</p>
                </div>
              </div>

              {/* Queries Per Minute Chart */}
              {connectionMetrics.queries_by_minute?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" />
                    Queries Per Minute (Last Hour)
                  </h4>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <MiniBarChart data={connectionMetrics.queries_by_minute} height={80} />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>{connectionMetrics.queries_by_minute[0]?.minute}</span>
                      <span>{connectionMetrics.queries_by_minute[connectionMetrics.queries_by_minute.length - 1]?.minute}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Response Time Distribution */}
              {connectionMetrics.rows_distribution?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    Response Time by Rows Fetched
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rows Range</TableHead>
                        <TableHead>Query Count</TableHead>
                        <TableHead>Avg Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {connectionMetrics.rows_distribution.map((bucket, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{bucket.range}</TableCell>
                          <TableCell>{bucket.count}</TableCell>
                          <TableCell className="text-muted-foreground">{formatTime(bucket.avg_time_ms)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Recent Queries */}
              {connectionMetrics.recent_queries?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-blue-500" />
                    Recent Queries
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Query</TableHead>
                        <TableHead>Rows</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Executed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {connectionMetrics.recent_queries.map((query, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {query.success ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs truncate font-mono text-xs">
                            {query.query}
                          </TableCell>
                          <TableCell>{query.row_count || 0}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatTime(query.execution_time_ms)}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {new Date(query.created_at).toLocaleTimeString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          ) : (
            <CardContent className="text-center py-12 text-muted-foreground">
              Select a connection to view detailed performance metrics
            </CardContent>
          )}
        </Card>

        {/* All Connections Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="w-4 h-4" />
              All Connections Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connection</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Database</TableHead>
                  <TableHead>Queries</TableHead>
                  <TableHead>Success Rate</TableHead>
                  <TableHead>Rows Fetched</TableHead>
                  <TableHead>Avg Time</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview?.connections?.map((conn) => (
                  <TableRow 
                    key={conn.connection_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedConnection(conn.connection_id)}
                  >
                    <TableCell className="font-medium">{conn.connection_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-${dbTypeColors[conn.db_type] || 'gray'}-500`}>
                        {conn.db_type.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{conn.database}</TableCell>
                    <TableCell>{formatNumber(conn.total_queries)}</TableCell>
                    <TableCell>
                      <span className={conn.success_rate >= 90 ? 'text-green-500' : conn.success_rate >= 70 ? 'text-yellow-500' : 'text-red-500'}>
                        {conn.success_rate}%
                      </span>
                    </TableCell>
                    <TableCell>{formatNumber(conn.total_rows_fetched)}</TableCell>
                    <TableCell className="text-muted-foreground">{formatTime(conn.avg_execution_time_ms)}</TableCell>
                    <TableCell>
                      <Badge variant={conn.status === 'connected' ? 'default' : 'secondary'}>
                        {conn.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {(!overview?.connections || overview.connections.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No connections found. Create a connection to start tracking metrics.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MetricsPage;
